import { inject, Injectable } from '@angular/core';
import { Order, QueryEntity, SortBy } from '@datorama/akita';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { naturalSort, valueGetterFnSort } from '@railmybox/shared/util';
import { StationsSortKeys, StationsState, StationsStore } from './stations.store';
import { AdminStationSearchParamModel, ChangeEntryModel, LoadpointModel, StationModel } from '@railmybox/api-booking';
import { StationOperatorModel } from '@railmybox/api-dispo';
import { StationStorageModel, StationSurchargeModel } from '@railmybox/api-pricing';

@Injectable({ providedIn: 'root' })
export class StationsQuery extends QueryEntity<StationsState> {
  readonly sortByFnMap: Record<StationsSortKeys, SortBy<StationModel, StationsState>> = {
    active: naturalSort('active'),
    name: naturalSort('name'),
    code: naturalSort('code'),
    unLoCode: naturalSort('unLoCode'),
    trucking: valueGetterFnSort(),
    combinedCapacity: valueGetterFnSort(),
    sea: valueGetterFnSort(),
  };

  constructor() {
    const store = inject(StationsStore);
    super(store);
  }

  public hasError(): Observable<boolean> {
    return this.selectError().pipe(map((err) => err != undefined));
  }

  public isLoading(): Observable<boolean> {
    return this.selectLoading();
  }

  sortedStations$(): Observable<StationModel[]> {
    return this.select('ui').pipe(
      switchMap((uiState) => {
        if (!uiState?.sortBy || !uiState?.sortDirection) {
          return this.selectAll();
        }

        return this.selectAll({
          sortBy: this.sortByFnMap[uiState?.sortBy],
          sortByOrder: uiState?.sortDirection as unknown as Order,
        });
      })
    );
  }

  getLoadpoints(): Observable<LoadpointModel[]> {
    return this.select('loadpoints');
  }

  getOperatorCodes(): Observable<StationOperatorModel[]> {
    return this.select('operatorCodes');
  }

  getAdditionalSurcharges(): Observable<StationSurchargeModel[]> {
    return this.select('additionalSurcharges');
  }

  getFees(): Observable<StationStorageModel[]> {
    return this.select('fees');
  }

  getSurcharges(): StationSurchargeModel[] {
    return this.getValue().additionalSurcharges as StationSurchargeModel[];
  }

  get filters() {
    return this.getValue().filters;
  }

  get filters$(): Observable<AdminStationSearchParamModel> {
    return this.select('filters');
  }

  get offset$(): Observable<number> {
    return this.select('offset');
  }

  get limit$(): Observable<number> {
    return this.select('limit');
  }

  setActive(entityId: string) {
    this.store.setActive(entityId);
  }

  getActiveStation(): Observable<StationModel> {
    return this.select('activeStation');
  }

  getStationHistoryLogs(): Observable<ChangeEntryModel[]> {
    return this.select('historyLogs');
  }

  getDisabledPrevBtn(): Observable<boolean> {
    return this.select('disbledPrevBtn');
  }

  getDisabledNextBtn(): Observable<boolean> {
    return this.select('disbledNextBtn');
  }
}
